import type { WikiBlock } from "../types";

/**
 * Methodology — how “smart hero AI” is made possible (not API dump).
 * Host SSOTs: Judgment Selling · Raid AI · Sim AI harness · Sim-First · 적→판단.
 * Player-facing: spell out Soft / 평타; no bare AA / Soft≪AA jargon.
 */
export const craftMethodBlocks: readonly WikiBlock[] = [
  {
    type: "callout",
    tone: "info",
    text: "궁극적 셀링: 전투 의사를 매우 노련한 팀처럼, 개인이든 팀으로든 다양한 역경을 이겨나가는 똑똑한 영웅. 이 페이지는 그 이상을 가능하게 만드는 방법론이다 — 구현 API 나열이 아니다.",
  },
  {
    type: "callout",
    tone: "info",
    text: "읽기 안내: Soft = 발밑에 깔리는 위험 원(살짝 가속하는 생존 바닥 포함). 평타 = 영웅이 때리는 사거리. 핵심 계약은 “기질 Soft가 평타 사거리보다 훨씬 작다”이다 — Soft만 비웠다고 평타 밖까지 도망친 척하면 안 된다.",
  },
  { type: "h2", text: "누구 / 언제" },
  {
    type: "ul",
    items: [
      "기획·플레이테스트: “왜 이 영웅이 똑똑해 보이는가”를 같은 언어로 맞출 때",
      "밸런스·AI: 승률·전멸 숫자만으로 패치하지 않고, 어떤 판단이 깨졌는지 찾을 때",
      "아닌 것: 전체 Soft·평타 상수표·프로브 CLI 매뉴얼 (정본은 호스트). 판매에 보이는 게이트만 Wiki 「재능 계산식」요약",
    ],
  },
  { type: "h2", text: "한 장 이상향" },
  {
    type: "p",
    text: "플레이어가 보는 것은 노련한 파티다. 수치 버프가 아니라, 읽히는 판단이 장면마다 쌓인다.",
  },
  {
    type: "ul",
    items: [
      "위협이 보이면 몸이 먼저 움직이고(직감), 살아남을 자리를 고르며(통찰), 부관의 자리 제안을 얼마나 따를지(유대)가 슬라이더로 예측된다",
      "“Soft가 평타보다 훨씬 작다”는 기질이 보인다 — Soft 위험 원만 빠져 평타 사거리 안에서 멈춰 맞지 않는다. 카이팅·탈출이 손맛으로 읽힌다",
      "왜 살았는지가 Soft 숨은 합성이나 치트로만 설명되면, 그 순간 셀링은 무너진다",
    ],
  },
  { type: "h2", text: "방법론 한 장" },
  {
    type: "p",
    text: "똑똑함은 한 번에 심지 않는다. 관찰 → 분해 → 가설 → Soft·평타 계약을 지키는 검증 → 셀링 문장 유지 확인을 한 바퀴로 돈다.",
  },
  {
    type: "table",
    headers: ["단계", "하는 일", "산출"],
    rows: [
      [
        "관찰",
        "프로브·다이브로 같은 시드·구간을 반복해 본다",
        "전멸 / 사망 원인 / 스테레오타입 장면",
      ],
      [
        "패턴 분해",
        "시나리오 카드로 “무슨 판단 상황이었나”를 자른다",
        "카드 종류 · 사망 직전 샘플",
      ],
      [
        "가설 레버",
        "판결 A / A′ / B…처럼 한 번에 손잡이 하나",
        "“이 레버가 그 판단을 고친다” 문장",
      ],
      [
        "검증",
        "Soft·평타 계약·재능 소유권을 깨지 않는 범위에서 재실행",
        "승률·사망이 변해도 셀링 문장이 같은지",
      ],
      [
        "셀링 유지",
        "직감·통찰·유대 예측 문장이 여전히 맞는가",
        "통과 / 금지 치트로 위장했는지 거절",
      ],
    ],
  },
  { type: "h2", text: "승률·사망 분석이 하는 일" },
  {
    type: "p",
    text: "전멸률·사망 원인·스테레오타입은 “밸런스 패치 핑계”가 아니다. 어떤 판단이 깨졌는지를 찾는 렌즈다. 숫자가 나쁠수록 레버를 크게 돌리는 게 아니라, 장면이 말하는 실패 종류를 고른다.",
  },
  {
    type: "callout",
    tone: "warning",
    text: "콜로서스 점프 사례(요약): 착지 직후 남은 체력 사망을 “급피 미개방”으로 읽으면 잘못된 레버로 간다. 관측상 다수가 이미 급피 중이었다 → 정보·목표 왜곡(잔챙이가 도주 목표를 훔침 등) → Soft가 평타보다 훨씬 작다는 계약을 지키는 레버(판결 A/A′ 계열). 착지 피해·속도 치트로 똑똑해 보이게 만들지 않는다.",
  },
  { type: "h2", text: "테스트·하네스가 판단을 지키는 이유" },
  {
    type: "p",
    text: "하네스는 영웅 판단을 측정 가능한 장면으로 고정한다. 같은 계약으로 플레이와 헤드리스가 말한다.",
  },
  {
    type: "table",
    headers: ["구성", "언제", "왜 판단에 먹히나"],
    rows: [
      [
        "웨이브 vs 보스전",
        "웨이브 팩 / 보스 스킬·텔레그래프",
        "위협 종류가 다르면 관측 경로를 섞지 않는다",
      ],
      [
        "최소 파티 vs 의도 12인",
        "최소 파티 재현 / 의도된 12인 편성",
        "스테레오타입이 “파티 크기 아티팩트”인지 가른다",
      ],
      [
        "다이브 / 리플레이 / 한 레버",
        "시드 재현 → 사망 직전 → Soft·평타 계약을 지키는 한 레버",
        "우연한 승률 출렁임과 판단 수리를 분리한다",
      ],
    ],
  },
  { type: "h2", text: "똑똑함을 깨는 금지" },
  {
    type: "p",
    text: "치트로 똑똑해 보이면 셀링이 붕괴한다. 슬라이더와 “Soft가 평타보다 훨씬 작다”는 계약으로 예측할 수 없는 “가짜 노련함”이 된다.",
  },
  {
    type: "table",
    headers: ["금지", "셀링이 깨지는 이유"],
    rows: [
      [
        "Soft→평타 치트 (Soft만 비워도 평타 밖처럼 행동)",
        "카이팅 손맛이 Soft·평타 기질로 안 읽힘",
      ],
      [
        "급피 이속 배수 · 극단 전투 계수",
        "재능 차이가 아니라 수치 치트로 보임",
      ],
      [
        "피격·킬러에 스킬·원인 귀속 없음",
        "잘못된 레버로 “고쳐” 셀링 문장을 오염",
      ],
      [
        "착지 피해·점프 위상으로 판단 실패 은폐",
        "장면이 거짓말함 — 다음 역경도 같은 방식으로 속임",
      ],
    ],
  },
  {
    type: "p",
    text: "상세 금지 표는 Wiki 「하지 말 것」과 Judgment Selling 금지 목록이 권위다.",
  },
  { type: "h2", text: "읽는이에게 — 다음에 볼 것" },
  {
    type: "ul",
    items: [
      "Hub Wiki: 「한 장 계약」·「하지 말 것」— 무엇을 파는지 / 무엇을 안 하는지",
      "Hub Wiki: 「직감」·「통찰」·「유대」·「위협·체력 반응」— 플레이에서 보이는 축",
      "Hub Wiki: 「재능 계산식」— 판매에 보이는 게이트·식 요약 (정본은 Judgment Selling)",
      "호스트(가볍게): Judgment Selling · Raid AI 판단 · Sim AI 하네스 · 적→영웅 판단 정본 — 전체 식·금지·오서는 그쪽",
    ],
  },
];
